import { createContext, useContext, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { formatCurrency } from "@foroba/ui";

/** Exchange rates vs EUR (1 EUR = X units of currency). Updated 2025-06. */
const RATES_VS_EUR: Record<string, number> = {
  EUR: 1.000,
  USD: 1.080,
  GBP: 0.852,
  CHF: 0.974,
  JPY: 163.5,
  SEK: 11.31,
  DKK: 7.460,
  NOK: 11.82,
  PLN: 4.253,
  CZK: 25.32,
  HUF: 390.2,
  RON: 4.972,
  CAD: 1.474,
  AUD: 1.650,
  BRL: 5.720,
  MXN: 18.60,
};

export const SUPPORTED_CURRENCIES = Object.keys(RATES_VS_EUR);

const ZERO_DECIMALS = new Set(["JPY", "HUF", "CZK"]);

interface CurrencyContextValue {
  displayCurrency: string;
  setDisplayCurrency: (c: string) => void;
  /** Convert cents from `fromCurrency` to display-currency cents */
  convert: (cents: number, fromCurrency: string) => number;
  /** Format cents in the display currency */
  /** Format cents in the display currency using the UI language (`1,234.50` en / `1.234,50` it). */
  fmt: (cents: number, fromCurrency?: string, opts?: { maximumFractionDigits?: number }) => string;
}

const CurrencyContext = createContext<CurrencyContextValue>({
  displayCurrency: "EUR",
  setDisplayCurrency: () => {},
  convert: (c) => c,
  fmt: (c) => formatCurrency(c / 100, "EUR", "en"),
});

export function useCurrency(): CurrencyContextValue {
  return useContext(CurrencyContext);
}

export function CurrencyProvider({ children }: { children: ReactNode }) {
  const { i18n } = useTranslation();
  const [displayCurrency, setDisplayCurrencyState] = useState<string>(() => {
    try {
      return localStorage.getItem("pazuru-currency") ?? "EUR";
    } catch {
      return "EUR";
    }
  });

  const setDisplayCurrency = (c: string) => {
    setDisplayCurrencyState(c);
    try { localStorage.setItem("pazuru-currency", c); } catch { /* ignore */ }
  };

  const convert = (cents: number, fromCurrency = "EUR"): number => {
    const from = RATES_VS_EUR[fromCurrency] ?? 1;
    const to = RATES_VS_EUR[displayCurrency] ?? 1;
    // cents → EUR → displayCurrency
    return Math.round((cents / from) * to);
  };

  const fmt = (cents: number, fromCurrency = "EUR", opts: { maximumFractionDigits?: number } = {}): string => {
    const major = convert(cents, fromCurrency) / 100;
    const maximumFractionDigits = opts.maximumFractionDigits ?? (ZERO_DECIMALS.has(displayCurrency) ? 0 : undefined);
    return formatCurrency(major, displayCurrency, i18n.language, { maximumFractionDigits });
  };

  return (
    <CurrencyContext.Provider value={{ displayCurrency, setDisplayCurrency, convert, fmt }}>
      {children}
    </CurrencyContext.Provider>
  );
}
